/**
 * UI Selectors Module
 * Handles location selection via panel-based UI
 *
 * Supports two modes:
 * 1. Spot-based (new): Grouped by region/area with search
 * 2. Station-based (legacy): Region → Area → Station hierarchy
 */

import {
    AppState,
    getCurrentRegionId,
    getCurrentAreaId,
    getAreasForCurrentRegion,
    getStationsForCurrentArea,
    isSpotMode,
    getCurrentSpotSlug,
    getCurrentLocationName
} from './state.js';
import { updateShareButtonText } from './share.js';
import { syncSignupFormLocation, fetchSpots } from './notifications.js';
import { loadSpots, loadRegions } from './config-loader.js';
import { UI_TIMING } from './time.js';
import { createSearchInput, clearChildren } from './dom-utils.js';
import { logger } from './logger.js';
import { getRecentSpots, closestSpots, formatMiles } from './recent-spots.js';
import { formatRegionName, formatAreaName, compareRegions } from '../shared/location-names.mjs';

// Panel state
let isPanelOpen = false;
let spotSearchQuery = '';

// Drill-down picker view state (spot mode only).
// mode/regionId describe the non-search view currently shown; preSearch
// remembers where the user was when a search began (restored on clear);
// nearby caches the "Spots near me" result for the current panel session.
const pickerView = {
    mode: 'home',       // 'home' | 'region'
    regionId: null,     // set when mode === 'region'
    preSearch: null,    // { mode, regionId } | null
    nearby: null        // null | { status: 'pending'|'ok'|'error', items?: [{spot, miles}] }
};

// regions.json data (display names + canonical region ordering for the picker)
let pickerRegionsData = null;

/**
 * Reset the picker to the HOME view (fresh navigation on every open)
 */
function resetPickerView() {
    pickerView.mode = 'home';
    pickerView.regionId = null;
    pickerView.preSearch = null;
    pickerView.nearby = null;
}

// Focus trap state (dialog a11y) - handler reference so close can remove it
let focusTrapHandler = null;

// Cache for spots data (shared with notifications.js via fetchSpots)
let spotsGroupedCache = null;

// Progressive rendering constants (reduces initial DOM size)
const INITIAL_RENDER_COUNT = 30;
const BATCH_SIZE = 30;

/**
 * Initialize the location panel and button
 */
export function initializeSelectors() {
    // Spot mode doesn't require legacy state
    if (!isSpotMode() && (!AppState.currentRegion || !AppState.regions)) {
        logger.warn('Cannot initialize selectors: state not ready');
        return;
    }

    // Set up event listeners
    setupPanelListeners();

    // Update the location button text
    updateLocationButtonText();

    // Update page title
    if (isSpotMode()) {
        updatePageTitleForSpot(AppState.currentSpot);
    } else {
        updatePageTitle(AppState.currentRegion, AppState.currentArea, AppState.currentStation);
    }
}

/**
 * Set up all panel-related event listeners
 */
function setupPanelListeners() {
    const locationButton = document.getElementById('location-button');
    const panelBackdrop = document.getElementById('panel-backdrop');
    const panelClose = document.getElementById('panel-close');

    if (locationButton) {
        locationButton.addEventListener('click', toggleLocationPanel);
    }

    if (panelBackdrop) {
        panelBackdrop.addEventListener('click', closeLocationPanel);
    }

    if (panelClose) {
        panelClose.addEventListener('click', closeLocationPanel);
    }

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && isPanelOpen) {
            closeLocationPanel();
        }
    });
}

/**
 * Get the currently focusable elements inside the panel (in DOM order)
 */
function getPanelFocusableElements(panel) {
    const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    return Array.from(panel.querySelectorAll(selector))
        .filter(el => !el.disabled && el.offsetParent !== null);
}

/**
 * Enable the Tab / Shift+Tab focus trap for the open panel (idempotent)
 */
function enableFocusTrap(panel) {
    if (focusTrapHandler) return;

    focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;

        const focusable = getPanelFocusableElements(panel);
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (e.shiftKey) {
            if (active === first || !panel.contains(active)) {
                e.preventDefault();
                last.focus();
            }
        } else if (active === last || !panel.contains(active)) {
            e.preventDefault();
            first.focus();
        }
    };

    document.addEventListener('keydown', focusTrapHandler);
}

/**
 * Remove the focus trap listener (safe to call when not active)
 */
function disableFocusTrap() {
    if (!focusTrapHandler) return;
    document.removeEventListener('keydown', focusTrapHandler);
    focusTrapHandler = null;
}

/**
 * Toggle the location panel open/closed
 */
export function toggleLocationPanel() {
    if (isPanelOpen) {
        closeLocationPanel();
    } else {
        openLocationPanel();
    }
}

/**
 * Open the location panel
 */
export async function openLocationPanel() {
    const panel = document.getElementById('location-panel');
    const button = document.getElementById('location-button');

    if (!panel) return;

    // Show panel immediately (content loads async for spot mode)
    panel.hidden = false;
    isPanelOpen = true;

    if (button) {
        button.setAttribute('aria-expanded', 'true');
    }

    // Prevent body scroll
    document.body.classList.add('no-scroll');

    // Move focus into the dialog immediately (close button); in spot mode the
    // search input takes focus once the spot list has loaded (below)
    const closeButton = document.getElementById('panel-close');
    if (closeButton) {
        closeButton.focus();
    }

    // Trap Tab / Shift+Tab within the panel while open
    enableFocusTrap(panel);

    // Populate panel content based on mode
    if (isSpotMode()) {
        // Every open starts fresh on HOME (also clears the nearby cache)
        resetPickerView();
        await populateSpotPanel();
        // Focus search after spots are loaded
        const searchInput = document.getElementById('spot-search');
        if (searchInput) {
            setTimeout(() => searchInput.focus(), UI_TIMING.focusDelay);
        }
    } else {
        populateLocationPanel();
    }
}

/**
 * Close the location panel
 */
export function closeLocationPanel() {
    const panel = document.getElementById('location-panel');
    const button = document.getElementById('location-button');

    if (!panel) return;

    panel.hidden = true;
    isPanelOpen = false;
    spotSearchQuery = '';

    if (button) {
        button.setAttribute('aria-expanded', 'false');
    }

    // Restore body scroll
    document.body.classList.remove('no-scroll');

    // Remove the focus trap and return focus to the trigger button
    disableFocusTrap();
    if (button) {
        button.focus();
    }
}

/**
 * Populate the spot picker panel (new spot-based UI)
 * Fetches spots from /api/spots to match email subscription form
 */
export async function populateSpotPanel() {
    const panelBody = document.getElementById('panel-body');
    if (!panelBody) return;

    clearChildren(panelBody);

    // Show loading state
    const loadingEl = document.createElement('div');
    loadingEl.className = 'spot-loading';
    loadingEl.textContent = 'Loading spots...';
    panelBody.appendChild(loadingEl);

    // Fetch spots from API (uses same endpoint as email subscription form),
    // with an offline fallback built from the static data/spots.json
    let grouped = spotsGroupedCache;
    if (!grouped) {
        try {
            const regions = await fetchSpots();
            if (regions.length > 0) {
                // Transform array format to grouped object format for renderSpotList
                grouped = {};
                for (const region of regions) {
                    grouped[region.id] = {};
                    for (const area of region.areas || []) {
                        grouped[region.id][area.id] = area.spots || [];
                    }
                }
            } else {
                // /api/spots unavailable (offline / API down): build the same
                // grouped structure from the bundled data/spots.json instead
                grouped = await buildGroupedSpotsFromStaticData();
            }
            if (grouped && Object.keys(grouped).length > 0) {
                spotsGroupedCache = grouped;
            } else {
                throw new Error('No spots available from API or static data');
            }
        } catch (error) {
            logger.error('Failed to fetch spots for location panel:', error);
            clearChildren(panelBody);
            const errorEl = document.createElement('div');
            errorEl.className = 'spot-no-results';
            errorEl.textContent = 'Failed to load spots. Please try again.';
            panelBody.appendChild(errorEl);
            return;
        }
    }

    // regions.json supplies region display names, area display names, and
    // the canonical region ordering for the HOME view (cached fetch; the
    // loader falls back to a minimal config if the file is unavailable)
    pickerRegionsData = await loadRegions();

    // Clear loading state
    clearChildren(panelBody);

    // Search box (using safe DOM method) — sticky across all picker views
    const searchContainer = document.createElement('div');
    searchContainer.className = 'spot-search-container';
    const searchInput = createSearchInput('spot-search', 'Search spots...', spotSearchQuery);
    searchContainer.appendChild(searchInput);
    panelBody.appendChild(searchContainer);

    // Search listener: any non-empty query switches to the flat filtered
    // list; clearing it restores the view the user was on before typing
    searchInput.addEventListener('input', (e) => {
        const previousQuery = spotSearchQuery;
        spotSearchQuery = e.target.value.toLowerCase();

        if (spotSearchQuery && !previousQuery) {
            // Entering search: remember where we were
            pickerView.preSearch = { mode: pickerView.mode, regionId: pickerView.regionId };
        } else if (!spotSearchQuery && previousQuery) {
            // Search cleared: restore the pre-search view
            const restore = pickerView.preSearch || { mode: 'home', regionId: null };
            pickerView.mode = restore.mode;
            pickerView.regionId = restore.regionId;
            pickerView.preSearch = null;
        }

        renderPickerView(panelBody);

        // Reset scroll when crossing the search boundary (view switch)
        if ((spotSearchQuery === '') !== (previousQuery === '')) {
            panelBody.scrollTop = 0;
        }
    });

    // Render the current picker view (home, region, or search results)
    renderPickerView(panelBody);
}

/**
 * Render whichever picker view the state calls for (search wins over mode)
 */
function renderPickerView(panelBody) {
    const grouped = spotsGroupedCache;
    if (!grouped) return;

    const currentSpotSlug = getCurrentSpotSlug();

    if (spotSearchQuery) {
        // SEARCH view: global flat filtered results (progressive rendering),
        // with region-name meta so cross-region results are distinguishable
        renderSpotList(panelBody, grouped, currentSpotSlug, spotSearchQuery, { showRegionMeta: true });
    } else if (pickerView.mode === 'region' && grouped[pickerView.regionId]) {
        renderRegionView(panelBody, grouped, currentSpotSlug);
    } else {
        pickerView.mode = 'home';
        pickerView.regionId = null;
        renderHomeView(panelBody, grouped, currentSpotSlug);
    }
}

/**
 * Get the display name for a region (regions.json name, else prettified id)
 */
function regionDisplayName(regionId) {
    return pickerRegionsData?.regions?.[regionId]?.name || formatRegionName(regionId);
}

/**
 * Get the display name for an area (regions.json name, else prettified id)
 */
function areaDisplayName(regionId, areaId) {
    return pickerRegionsData?.regions?.[regionId]?.areas?.[areaId]?.name || formatAreaName(areaId);
}

/**
 * Create a picker section label ("Recent", "Nearby", "Regions")
 */
function createSectionLabel(text) {
    const label = document.createElement('div');
    label.className = 'panel-section-label';
    label.textContent = text;
    return label;
}

/**
 * Remove the current list (any view) from the panel body and mount a new one
 */
function replaceSpotList(panelBody) {
    const existingList = panelBody.querySelector('.spot-list');
    if (existingList) {
        existingList.remove();
    }
    const list = document.createElement('div');
    list.className = 'spot-list';
    return list;
}

/**
 * Regions for the HOME view: regions.json order first, then any extra
 * regions present in the spots data (California-first alphabetical)
 * @returns {Array<{id: string, name: string, count: number}>}
 */
function orderedRegionEntries(grouped) {
    const buildEntry = (id) => ({
        id,
        name: regionDisplayName(id),
        count: Object.values(grouped[id]).reduce((sum, spots) => sum + spots.length, 0)
    });

    const ordered = [];
    const seen = new Set();
    for (const id of Object.keys(pickerRegionsData?.regions || {})) {
        if (grouped[id]) {
            ordered.push(buildEntry(id));
            seen.add(id);
        }
    }
    const extras = Object.keys(grouped).filter(id => !seen.has(id)).sort(compareRegions);
    for (const id of extras) {
        ordered.push(buildEntry(id));
    }
    return ordered;
}

/**
 * Create a region row for the HOME view (name + count chip + chevron)
 */
function createRegionItem(entry, panelBody) {
    const item = document.createElement('button');
    item.className = 'region-item';
    item.dataset.regionId = entry.id;
    item.setAttribute('aria-label', `${entry.name}, ${entry.count} spots`);

    const nameSpan = document.createElement('span');
    nameSpan.className = 'region-name';
    nameSpan.textContent = entry.name;

    const metaSpan = document.createElement('span');
    metaSpan.className = 'spot-meta';

    const countChip = document.createElement('span');
    countChip.className = 'region-count';
    countChip.textContent = String(entry.count);
    metaSpan.appendChild(countChip);

    const chevron = document.createElement('span');
    chevron.className = 'region-chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.textContent = '›';
    metaSpan.appendChild(chevron);

    item.appendChild(nameSpan);
    item.appendChild(metaSpan);

    item.addEventListener('click', () => enterRegion(entry.id, panelBody));
    return item;
}

/**
 * HOME view: Recent (if any) → Nearby → Regions
 */
function renderHomeView(panelBody, grouped, currentSpotSlug) {
    const list = replaceSpotList(panelBody);

    // Recent section (omitted entirely when empty)
    const recents = getRecentSpots();
    if (recents.length > 0) {
        list.appendChild(createSectionLabel('Recent'));
        for (const recent of recents) {
            list.appendChild(createSpotItem(recent, currentSpotSlug, {
                text: regionDisplayName(recent.region),
                className: 'spot-meta-region'
            }));
        }
    }

    // Nearby section (button → rows/message after a location request)
    list.appendChild(createSectionLabel('Nearby'));
    const nearbySection = document.createElement('div');
    nearbySection.className = 'nearby-section';
    renderNearbySection(nearbySection);
    list.appendChild(nearbySection);

    // Regions section (drill-down entry points)
    list.appendChild(createSectionLabel('Regions'));
    for (const entry of orderedRegionEntries(grouped)) {
        list.appendChild(createRegionItem(entry, panelBody));
    }

    panelBody.appendChild(list);
}

/**
 * Fill the Nearby section based on the cached per-session state
 */
function renderNearbySection(sectionEl) {
    clearChildren(sectionEl);
    const state = pickerView.nearby;

    if (state?.status === 'ok') {
        const currentSpotSlug = getCurrentSpotSlug();
        for (const { spot, miles } of state.items) {
            sectionEl.appendChild(createSpotItem(spot, currentSpotSlug, {
                text: formatMiles(miles),
                className: 'spot-distance'
            }));
        }
    } else if (state?.status === 'error') {
        const message = document.createElement('div');
        message.className = 'nearby-message';
        message.textContent = 'Location unavailable';
        sectionEl.appendChild(message);
    } else {
        const button = document.createElement('button');
        button.className = 'nearby-btn';
        if (state?.status === 'pending') {
            button.disabled = true;
            button.textContent = 'Locating…';
        } else {
            button.textContent = '\u{1F4CD} Spots near me';
        }
        button.addEventListener('click', handleNearbyRequest);
        sectionEl.appendChild(button);
    }
}

/**
 * Re-render the Nearby section in place if it is still on screen
 * (the user may have drilled into a region or typed a search meanwhile)
 */
function refreshNearbySection() {
    if (!isPanelOpen || spotSearchQuery || pickerView.mode !== 'home') return;
    const sectionEl = document.querySelector('#panel-body .nearby-section');
    if (sectionEl) {
        renderNearbySection(sectionEl);
    }
}

/**
 * Promise wrapper around geolocation (never auto-requested; tap-only)
 */
function getCurrentPositionOnce() {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocation not supported'));
            return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 8000,
            maximumAge: 5 * 60 * 1000
        });
    });
}

/**
 * "Spots near me" tap: locate, compute the 5 closest spots, cache for the
 * rest of the panel session (cache is cleared on the next panel open)
 */
async function handleNearbyRequest() {
    if (pickerView.nearby?.status === 'pending') return;

    pickerView.nearby = { status: 'pending' };
    refreshNearbySection();

    try {
        const position = await getCurrentPositionOnce();
        // Static spots.json carries lat/lon for every spot (cached fetch)
        const spotsData = await loadSpots();
        const items = closestSpots(
            position.coords.latitude,
            position.coords.longitude,
            spotsData.spots || [],
            5
        );
        pickerView.nearby = { status: 'ok', items };
    } catch (error) {
        // Denied/timeout/unsupported — show the muted one-liner
        logger.log('Nearby spots unavailable:', error?.message || error);
        pickerView.nearby = { status: 'error' };
    }

    refreshNearbySection();
}

/**
 * Drill into a region: switch state, render, focus the back row
 */
function enterRegion(regionId, panelBody) {
    pickerView.mode = 'region';
    pickerView.regionId = regionId;
    renderPickerView(panelBody);
    panelBody.scrollTop = 0;

    const backRow = panelBody.querySelector('.panel-back');
    if (backRow) {
        backRow.focus();
    }
}

/**
 * Back to HOME: restore scroll to top and focus the region row we came
 * from (falls back to the search input)
 */
function leaveRegion(panelBody, fromRegionId) {
    pickerView.mode = 'home';
    pickerView.regionId = null;
    renderPickerView(panelBody);
    panelBody.scrollTop = 0;

    const originRow = panelBody.querySelector(`.region-item[data-region-id="${fromRegionId}"]`);
    const fallback = document.getElementById('spot-search');
    const target = originRow || fallback;
    if (target) {
        target.focus();
    }
}

/**
 * REGION view: back row + region title + area-grouped spot rows
 * Rendered directly (a region tops out around 63 rows — no progressive
 * rendering needed at this size)
 */
function renderRegionView(panelBody, grouped, currentSpotSlug) {
    const list = replaceSpotList(panelBody);
    const regionId = pickerView.regionId;
    const areas = grouped[regionId] || {};

    const backRow = document.createElement('button');
    backRow.className = 'panel-back';
    backRow.textContent = '‹ All regions';
    backRow.addEventListener('click', () => leaveRegion(panelBody, regionId));
    list.appendChild(backRow);

    const title = document.createElement('div');
    title.className = 'spot-region-header';
    title.textContent = regionDisplayName(regionId);
    list.appendChild(title);

    for (const areaId of Object.keys(areas).sort()) {
        const areaHeader = document.createElement('div');
        areaHeader.className = 'spot-area-header';
        areaHeader.textContent = areaDisplayName(regionId, areaId);
        list.appendChild(areaHeader);

        for (const spot of areas[areaId]) {
            list.appendChild(createSpotItem(spot, currentSpotSlug));
        }
    }

    panelBody.appendChild(list);
}

/**
 * Offline fallback: build the grouped spot structure from data/spots.json
 * (same shape as the /api/spots transform above: region → area → [spots]).
 * spots.json is precached by the service worker, so this works offline.
 * @returns {Promise<Object>} Grouped spots or empty object
 */
async function buildGroupedSpotsFromStaticData() {
    const spotsData = await loadSpots();
    const grouped = {};
    for (const spot of spotsData.spots || []) {
        if (!grouped[spot.region]) grouped[spot.region] = {};
        if (!grouped[spot.region][spot.area]) grouped[spot.region][spot.area] = [];
        grouped[spot.region][spot.area].push({
            slug: spot.slug,
            name: spot.name,
            breakType: spot.breakType,
            skillLevel: spot.skillLevel
        });
    }
    return grouped;
}

// Human-readable names imported from shared/location-names.mjs

/**
 * Create a spot item button element (using safe DOM methods)
 * @param {Object} spot - Spot data ({ slug, name, breakType?, skillLevel? })
 * @param {string} currentSpotSlug - Currently selected spot slug
 * @param {{text: string, className: string}|null} metaOverride - Replace the
 *   break/skill chips with a single chip (region name, distance, ...)
 */
function createSpotItem(spot, currentSpotSlug, metaOverride = null) {
    const spotItem = document.createElement('button');
    spotItem.className = 'spot-item';
    if (spot.slug === currentSpotSlug) {
        spotItem.classList.add('selected');
    }

    // Use safe DOM construction instead of innerHTML
    const nameSpan = document.createElement('span');
    nameSpan.className = 'spot-name';
    nameSpan.textContent = spot.name;

    const metaSpan = document.createElement('span');
    metaSpan.className = 'spot-meta';

    if (metaOverride) {
        if (metaOverride.text) {
            const chip = document.createElement('span');
            chip.className = metaOverride.className;
            chip.textContent = metaOverride.text;
            metaSpan.appendChild(chip);
        }
    } else {
        if (spot.breakType) {
            const breakTypeSpan = document.createElement('span');
            breakTypeSpan.className = 'spot-break-type';
            breakTypeSpan.textContent = spot.breakType;
            metaSpan.appendChild(breakTypeSpan);
        }

        if (spot.skillLevel) {
            const skillSpan = document.createElement('span');
            skillSpan.className = 'spot-skill';
            skillSpan.textContent = spot.skillLevel;
            metaSpan.appendChild(skillSpan);
        }
    }

    spotItem.appendChild(nameSpan);
    spotItem.appendChild(metaSpan);

    spotItem.addEventListener('click', () => handleSpotSelect(spot.slug));
    return spotItem;
}

/**
 * Flatten grouped spots into a renderable list with headers
 * Returns array of { type: 'region'|'area'|'spot', data: ... }
 */
function flattenGroupedSpots(grouped, query) {
    const items = [];

    // Sort regions (California first, then alphabetically) - uses shared compare function
    const sortedRegions = Object.keys(grouped).sort(compareRegions);

    for (const region of sortedRegions) {
        const areas = grouped[region];
        const regionItems = [];

        // Sort areas alphabetically
        const sortedAreas = Object.keys(areas).sort();

        for (const area of sortedAreas) {
            const spots = areas[area];
            const areaSpots = [];

            for (const spot of spots) {
                // Filter by search query
                if (query) {
                    const matchName = spot.name.toLowerCase().includes(query);
                    const matchArea = area.toLowerCase().includes(query);
                    const matchRegion = region.toLowerCase().includes(query);
                    if (!matchName && !matchArea && !matchRegion) {
                        continue;
                    }
                }
                // Carry the region id so search results can show region meta
                areaSpots.push({ type: 'spot', data: spot, region });
            }

            if (areaSpots.length > 0) {
                regionItems.push({ type: 'area', data: { id: area, name: formatAreaName(area) } });
                regionItems.push(...areaSpots);
            }
        }

        if (regionItems.length > 0) {
            items.push({ type: 'region', data: { id: region, name: formatRegionName(region) } });
            items.push(...regionItems);
        }
    }

    return items;
}

/**
 * Render the filtered spot list with progressive loading
 * Only renders INITIAL_RENDER_COUNT items initially, loads more on scroll
 * @param {Object} options
 * @param {boolean} options.showRegionMeta - Show the region name in the meta
 *   position instead of break/skill chips (used by the SEARCH view)
 */
function renderSpotList(container, grouped, currentSpotSlug, query, options = {}) {
    const listContainer = replaceSpotList(container);

    // Flatten spots into renderable items
    const allItems = flattenGroupedSpots(grouped, query);
    const totalSpots = allItems.filter(item => item.type === 'spot').length;

    // No results message
    if (totalSpots === 0) {
        const noResults = document.createElement('div');
        noResults.className = 'spot-no-results';
        noResults.textContent = query
            ? `No spots found matching "${query}"`
            : 'No spots available';
        listContainer.appendChild(noResults);
        container.appendChild(listContainer);
        return;
    }

    // Progressive rendering state
    let renderedCount = 0;
    let currentIndex = 0;

    /**
     * Render a batch of items
     */
    function renderBatch(count) {
        const fragment = document.createDocumentFragment();
        let batchRendered = 0;

        while (currentIndex < allItems.length && batchRendered < count) {
            const item = allItems[currentIndex];

            if (item.type === 'region') {
                const regionHeader = document.createElement('div');
                regionHeader.className = 'spot-region-header';
                regionHeader.textContent = item.data.name;
                fragment.appendChild(regionHeader);
            } else if (item.type === 'area') {
                const areaHeader = document.createElement('div');
                areaHeader.className = 'spot-area-header';
                areaHeader.textContent = item.data.name;
                fragment.appendChild(areaHeader);
            } else if (item.type === 'spot') {
                const metaOverride = options.showRegionMeta
                    ? { text: regionDisplayName(item.region), className: 'spot-meta-region' }
                    : null;
                const spotItem = createSpotItem(item.data, currentSpotSlug, metaOverride);
                fragment.appendChild(spotItem);
                renderedCount++;
                batchRendered++;
            }

            currentIndex++;
        }

        // Remove loading indicator before appending new items
        const loadingIndicator = listContainer.querySelector('.spot-loading-more');
        if (loadingIndicator) {
            loadingIndicator.remove();
        }

        listContainer.appendChild(fragment);

        // Add loading indicator if more items remain
        if (currentIndex < allItems.length) {
            const loadingMore = document.createElement('div');
            loadingMore.className = 'spot-loading-more';
            loadingMore.textContent = `Loading more... (${renderedCount}/${totalSpots})`;
            listContainer.appendChild(loadingMore);
        }
    }

    // Render initial batch
    renderBatch(INITIAL_RENDER_COUNT);

    // Setup IntersectionObserver for progressive loading
    if (currentIndex < allItems.length) {
        const loadMoreObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && currentIndex < allItems.length) {
                    renderBatch(BATCH_SIZE);

                    // Disconnect if all items rendered
                    if (currentIndex >= allItems.length) {
                        loadMoreObserver.disconnect();
                    }
                }
            });
        }, { rootMargin: '100px' });

        // Observe the loading indicator
        const observer = () => {
            const loadingIndicator = listContainer.querySelector('.spot-loading-more');
            if (loadingIndicator) {
                loadMoreObserver.observe(loadingIndicator);
            }
        };

        // Start observing after initial render
        requestAnimationFrame(observer);
    }

    container.appendChild(listContainer);
}

/**
 * Handle spot selection
 */
function handleSpotSelect(spotSlug) {
    if (window.switchSpot) {
        window.switchSpot(spotSlug);
    }
    closeLocationPanel();
    updateLocationButtonText();
    syncSignupFormLocation();
}

/**
 * Populate the location panel with region/area/station options (legacy mode)
 * Always shows all three levels for consistent hierarchy, even with single options
 */
export function populateLocationPanel() {
    const panelBody = document.getElementById('panel-body');
    if (!panelBody) return;

    clearChildren(panelBody);

    const currentRegionId = getCurrentRegionId();
    const currentAreaId = getCurrentAreaId();
    const currentStationId = AppState.currentStationId;

    // Always show all three levels for clarity
    const regions = AppState.regions || {};
    const areas = getAreasForCurrentRegion();
    const stations = getStationsForCurrentArea();

    // Region section - always show, mark as "locked" if single option
    const regionGroup = createLocationGroup('Region', regions, currentRegionId, 'region');
    panelBody.appendChild(regionGroup);

    // Area section - always show
    const areaGroup = createLocationGroup('Area', areas, currentAreaId, 'area');
    panelBody.appendChild(areaGroup);

    // Station section - always show
    const stationGroup = createLocationGroup('Station', stations, currentStationId, 'station');
    panelBody.appendChild(stationGroup);
}

/**
 * Create a location group (Region, Area, or Station section)
 * Handles single-option cases with "only" indicator
 */
function createLocationGroup(label, items, selectedId, type) {
    const group = document.createElement('div');
    group.className = 'location-group';

    const itemCount = Object.keys(items).length;
    const isSingleOption = itemCount === 1;

    if (isSingleOption) {
        group.classList.add('single-option');
    }

    // Label with count indicator
    const labelEl = document.createElement('span');
    labelEl.className = 'location-group-label';
    labelEl.textContent = label;
    group.appendChild(labelEl);

    // Options container
    const options = document.createElement('div');
    options.className = 'location-options';

    Object.entries(items).forEach(([id, item]) => {
        const button = document.createElement('button');
        button.className = 'location-option';
        button.textContent = item.shortName || item.name;
        button.dataset.id = id;
        button.dataset.type = type;

        if (id === selectedId) {
            button.classList.add('selected');
        }

        // Single option is always selected and non-interactive
        if (isSingleOption) {
            button.classList.add('only-option');
            button.disabled = true;
        }

        button.addEventListener('click', () => handleLocationSelect(type, id));
        options.appendChild(button);
    });

    group.appendChild(options);
    return group;
}

/**
 * Handle selection of a location option (legacy mode)
 * Auto-cascades through single-option selections for better UX
 */
function handleLocationSelect(type, id) {
    switch (type) {
        case 'region': {
            // Call global switchRegion function
            if (window.switchRegion) {
                window.switchRegion(id);
            }
            // Check if there's only one area - auto-select it
            const areas = getAreasForCurrentRegion();
            const areaIds = Object.keys(areas);
            if (areaIds.length === 1) {
                // Auto-select the only area and continue
                handleLocationSelect('area', areaIds[0]);
                return;
            }
            // Repopulate panel to show updated areas/stations
            populateLocationPanel();
            break;
        }

        case 'area': {
            // Call global switchArea function
            if (window.switchArea) {
                window.switchArea(id);
            }
            // Check if there's only one station - auto-select it
            const stations = getStationsForCurrentArea();
            const stationIds = Object.keys(stations);
            if (stationIds.length === 1) {
                // Auto-select the only station and close
                handleLocationSelect('station', stationIds[0]);
                return;
            }
            // Repopulate panel to show updated stations
            populateLocationPanel();
            break;
        }

        case 'station':
            // Call global switchStation function
            if (window.switchStation) {
                window.switchStation(id);
            }
            // Close panel after station selection (final step)
            closeLocationPanel();
            break;
    }

    // Update button text
    updateLocationButtonText();

    // Sync signup form with new location
    syncSignupFormLocation();
}

/**
 * Update the location button text to show current spot/station
 */
export function updateLocationButtonText() {
    const locationText = document.getElementById('location-text');
    if (!locationText) return;

    const name = getCurrentLocationName();
    locationText.textContent = name || 'Select Location';
}

/**
 * Update the page title for spot mode
 */
export function updatePageTitleForSpot(spot) {
    if (!spot) {
        document.title = 'Surf Report';
        return;
    }

    const spotName = spot.name;
    const areaName = spot.area ? formatAreaName(spot.area) : '';

    if (areaName) {
        document.title = `${spotName} - ${areaName} | Surf Report`;
    } else {
        document.title = `${spotName} | Surf Report`;
    }
}

/**
 * Update the page title based on current region, area, and station (legacy)
 */
export function updatePageTitle(region, area, station) {
    // Update document title (browser tab)
    const stationName = station?.name || 'Station';
    const areaName = area?.shortName || area?.name || '';
    const regionName = region?.shortName || region?.name || '';

    if (areaName) {
        document.title = `${stationName} - ${areaName} | Surf Report`;
    } else if (regionName) {
        document.title = `${stationName} - ${regionName} | Surf Report`;
    } else {
        document.title = `${stationName} | Surf Report`;
    }
}

/**
 * Refresh selectors when navigation occurs
 */
export async function refreshSelectors(_regionId, _areaId, _stationId) {
    updateLocationButtonText();
    updateShareButtonText();

    if (isSpotMode()) {
        updatePageTitleForSpot(AppState.currentSpot);
    } else {
        updatePageTitle(AppState.currentRegion, AppState.currentArea, AppState.currentStation);
    }

    // If panel is open, repopulate it
    if (isPanelOpen) {
        if (isSpotMode()) {
            await populateSpotPanel();
        } else {
            populateLocationPanel();
        }
    }
}

/**
 * Refresh selectors for spot mode
 */
export async function refreshSelectorsForSpot(spot) {
    updateLocationButtonText();
    updateShareButtonText();
    updatePageTitleForSpot(spot);

    if (isPanelOpen) {
        await populateSpotPanel();
    }
}

// Legacy exports for backwards compatibility (can be removed later)
export function renderLocationSelector() {
    // No longer used - kept for compatibility
    updateLocationButtonText();
}
